import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { AddOrgGroupsContactsApiArg } from '@/__generated/es-api'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

type AddToGroupInput = {
  groupIds: Array<number | string>
  isAllContacts: boolean
  contactIds: string[]
  excludeIds: string[]
  includedGroupIds: string[]
  search: string
}

export const useAddToGroup = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const [mutate, res] = esApi.endpoints.addOrgGroupsContacts.useMutation()

  const addToGroup = useCallback(
    async ({ groupIds, isAllContacts, contactIds, excludeIds, includedGroupIds, search }: AddToGroupInput) => {
      const body: AddOrgGroupsContactsApiArg['body'] = {
        groupIds: groupIds.map((id) => Number(id)),
        contacts: {
          allContactsAreSelected: isAllContacts,
          includedContactIds: contactIds.map((id) => Number(id)),
          excludedContactIds: excludeIds.map((id) => Number(id)),
          includedGroupIds: includedGroupIds.map((id) => Number(id)),
          search,
        },
      }

      try {
        const response = await mutate({ orgId: orgId.toString(), body }).unwrap()
        const notificationKey = contactIds.join('-')
        createNotification({
          [`add-to-group-${notificationKey}`]: {
            type: 'success',
            message: response.backgroundTask
              ? 'Contacts are being added to the selected group(s). This process can take a couple of minutes to complete'
              : 'Contact(s) successfully added to the selected group(s)',
          },
        })
        return true
      } catch (error) {
        dispatch(
          createNotification({
            'add-to-group-failure': {
              type: 'error',
              message: getErrorMessage(error, "Contact(s) weren't added to selected group(s). Please try again"),
            },
          }),
        )
        return false
      }
    },
    [mutate, orgId, dispatch],
  )

  return [addToGroup, res] as const
}
